import { useEffect, useMemo, useState } from 'react'

import { createStake, listStakes, unstakeStake, type WalletStake } from '../../api/walletStakes'
import StakingCreateModal from './StakingCreateModal'
import StakingStakeCard from './StakingStakeCard'
import UnstakeConfirmModal from './UnstakeConfirmModal'

function formatNextUnlock(iso?: string | null) {
  if (!iso) return '-'
  const dt = new Date(iso)
  if (Number.isNaN(dt.getTime())) return '-'
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(dt)
}

function sortStakes(stakes: WalletStake[]) {
  return [...stakes].sort((a, b) => {
    const aUnlockable = a.status === 'active' && a.is_unlockable ? 0 : 1
    const bUnlockable = b.status === 'active' && b.is_unlockable ? 0 : 1
    if (aUnlockable !== bUnlockable) return aUnlockable - bUnlockable

    const aTime = new Date(a.locked_until).getTime()
    const bTime = new Date(b.locked_until).getTime()
    if (aTime !== bTime) return aTime - bTime

    return Number(b.amount_pixe) - Number(a.amount_pixe)
  })
}

export default function WalletStaking() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [unstakeOpen, setUnstakeOpen] = useState(false)
  const [unstakeBusy, setUnstakeBusy] = useState(false)
  const [selectedStake, setSelectedStake] = useState<WalletStake | null>(null)
  const [summary, setSummary] = useState<{
    available_pixe: string
    staked_pixe_total: string
    active_stakes_count: number
    next_unlock_at: string | null
  } | null>(null)
  const [stakes, setStakes] = useState<WalletStake[]>([])

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await listStakes()
      setSummary(data.summary)
      setStakes(data.stakes || [])
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load stakes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const sortedStakes = useMemo(() => sortStakes(stakes), [stakes])
  const available = Number(summary?.available_pixe || 0)

  async function onCreate(payload: { amount_pixe: string; lock_seconds: number; purpose: string }) {
    const res = await createStake(payload)
    setSummary(res.summary)
    await load()
    const until = new Date(res.stake.locked_until).toLocaleString()
    setMessage(`Staked ${Number(res.stake.amount_pixe).toLocaleString()} PIXE until ${until}`)
    window.setTimeout(() => setMessage(null), 2500)
  }

  function openUnstake(stake: WalletStake) {
    if (!stake.is_unlockable || stake.status !== 'active') return
    setSelectedStake(stake)
    setUnstakeOpen(true)
  }

  async function confirmUnstake() {
    if (!selectedStake) return
    setUnstakeBusy(true)
    setError(null)
    try {
      const res = await unstakeStake(selectedStake.id)
      setSummary(res.summary)
      setMessage(`Unstaked ${Number(res.stake.amount_pixe).toLocaleString()} PIXE`)
      window.setTimeout(() => setMessage(null), 2500)
      setUnstakeOpen(false)
      setSelectedStake(null)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to unstake')
    } finally {
      setUnstakeBusy(false)
    }
  }

  if (loading) {
    return <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">Loading staking...</div>
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-white">Stake PIXE</div>
            <div className="text-sm text-white/70">Lock funds for feature access</div>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            className="rounded-lg border border-sky-500/40 bg-sky-600/20 px-3 py-1.5 text-sm font-semibold text-sky-100 hover:bg-sky-600/30"
          >
            Create Stake
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="text-xs text-white/60">Available PIXE</div>
            <div className="text-lg font-semibold text-white">{Number(summary?.available_pixe || 0).toLocaleString()}</div>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="text-xs text-white/60">Staked PIXE</div>
            <div className="text-lg font-semibold text-white">{Number(summary?.staked_pixe_total || 0).toLocaleString()}</div>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="text-xs text-white/60">Active Stakes</div>
            <div className="text-lg font-semibold text-white">{summary?.active_stakes_count || 0}</div>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="text-xs text-white/60">Next Unlock</div>
            <div className="text-sm font-semibold text-white">{formatNextUnlock(summary?.next_unlock_at)}</div>
          </div>
        </div>
      </div>

      {error ? <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">{error}</div> : null}
      {message ? <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-100">{message}</div> : null}

      {sortedStakes.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/5 p-5 text-sm text-white/75">No stakes yet.</div>
      ) : (
        <div className="space-y-3">
          {sortedStakes.map((stake) => (
            <StakingStakeCard
              key={stake.id}
              stake={stake}
              onUnstake={openUnstake}
              busy={unstakeBusy && selectedStake?.id === stake.id}
            />
          ))}
        </div>
      )}

      <StakingCreateModal
        open={createOpen}
        availablePixe={available}
        onClose={() => setCreateOpen(false)}
        onSubmit={onCreate}
      />
      <UnstakeConfirmModal
        open={unstakeOpen}
        stake={selectedStake}
        busy={unstakeBusy}
        onClose={() => {
          if (unstakeBusy) return
          setUnstakeOpen(false)
          setSelectedStake(null)
        }}
        onConfirm={confirmUnstake}
      />
    </div>
  )
}
